import React from 'react';
import * as THREE from 'three';
import { darkenColor } from './utils';

/**
 * Large / one-off outdoor structures — main stage, food truck, kids
 * playground. Each function returns a `<group>` and shares the same
 * props bag as `tents.js`.
 */

// ---- Main Stage (pentas_utama) ------------------------------------
export function PentasUtama({ w, d, h, baseColor, emissive }) {
  return (
    <group>
      <mesh position={[0, 0.6, 0]} castShadow receiveShadow>
        <boxGeometry args={[w, 1.2, d]} />
        <meshStandardMaterial color="#1e293b" roughness={0.6} />
      </mesh>
      <mesh position={[0, 0.6, d/2 + 0.05]}>
        <boxGeometry args={[w, 1.2, 0.05]} />
        <meshStandardMaterial color={baseColor} emissive={baseColor} emissiveIntensity={0.4} />
      </mesh>
      <mesh position={[0, h * 1.4, -d/2]} castShadow>
        <boxGeometry args={[w * 1.1, h * 2.4, 0.3]} />
        <meshStandardMaterial color="#0f172a" emissive="#3b82f6" emissiveIntensity={0.35} />
      </mesh>
      <mesh position={[0, h * 1.4, -d/2 + 0.16]}>
        <boxGeometry args={[w * 0.95, h * 2.1, 0.02]} />
        <meshStandardMaterial color="#0ea5e9" emissive="#0ea5e9" emissiveIntensity={emissive + 0.6} />
      </mesh>
      {[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([x, z]) => (
        <mesh key={`tr-${x}-${z}`} position={[x * w / 2, h * 1.4, z * d / 2]} castShadow>
          <boxGeometry args={[0.25, h * 2.8, 0.25]} />
          <meshStandardMaterial color="#4b5563" metalness={0.8} roughness={0.3} />
        </mesh>
      ))}
      <mesh position={[0, h * 2.8, 0]} castShadow>
        <boxGeometry args={[w + 0.5, 0.3, d + 0.5]} />
        <meshStandardMaterial color="#4b5563" metalness={0.8} />
      </mesh>
      {[-w * 0.3, 0, w * 0.3].map((x) => (
        <mesh key={`lt-${x}`} position={[x, h * 2.6, d / 2]}>
          <sphereGeometry args={[0.2, 8, 8]} />
          <meshStandardMaterial color="#fef3c7" emissive="#fef3c7" emissiveIntensity={1.5} />
        </mesh>
      ))}
      <mesh position={[0, 0.3, d / 2 + 0.6]}>
        <boxGeometry args={[w * 0.4, 0.6, 1]} />
        <meshStandardMaterial color="#334155" />
      </mesh>
    </group>
  );
}

// ---- Food Truck ---------------------------------------------------
export function FoodTruck({ w, d, h, baseColor, emissive }) {
  return (
    <group>
      {[[-w * 0.35, -d * 0.35], [w * 0.35, -d * 0.35], [-w * 0.35, d * 0.35], [w * 0.35, d * 0.35]].map(([wx, wz]) => (
        <mesh key={`wh-${wx}-${wz}`} position={[wx, 0.4, wz]} rotation={[0, 0, Math.PI / 2]} castShadow>
          <cylinderGeometry args={[0.4, 0.4, 0.3, 12]} />
          <meshStandardMaterial color="#111827" roughness={0.9} />
        </mesh>
      ))}
      <mesh position={[0, h / 2 + 0.4, 0]} castShadow>
        <boxGeometry args={[w, h, d]} />
        <meshStandardMaterial color={baseColor} emissive={baseColor} emissiveIntensity={emissive * 0.3} roughness={0.5} />
      </mesh>
      <mesh position={[0, h / 2 + 0.4, d / 2 + 0.5]} castShadow>
        <boxGeometry args={[w * 0.9, h * 0.75, 1]} />
        <meshStandardMaterial color={darkenColor(baseColor, 0.15)} />
      </mesh>
      <mesh position={[0, h * 0.75 + 0.4, d / 2 + 1.01]}>
        <boxGeometry args={[w * 0.75, h * 0.35, 0.05]} />
        <meshStandardMaterial color="#0ea5e9" transparent opacity={0.55} metalness={0.6} />
      </mesh>
      <mesh position={[w / 2 + 0.02, h * 0.55 + 0.4, 0]}>
        <boxGeometry args={[0.05, h * 0.35, d * 0.55]} />
        <meshStandardMaterial color="#0f172a" />
      </mesh>
      <mesh position={[w / 2 + 0.5, h * 0.32 + 0.4, 0]}>
        <boxGeometry args={[1, 0.1, d * 0.55]} />
        <meshStandardMaterial color="#e2e8f0" />
      </mesh>
      <mesh position={[w / 2 + 0.7, h * 0.8 + 0.4, 0]} rotation={[0, 0, -Math.PI / 8]} castShadow>
        <boxGeometry args={[1.5, 0.05, d * 0.6]} />
        <meshStandardMaterial color="#dc2626" side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, h + 0.65, 0]} castShadow>
        <boxGeometry args={[w * 0.6, 0.7, 0.1]} />
        <meshStandardMaterial color="#fbbf24" emissive="#fbbf24" emissiveIntensity={0.4} />
      </mesh>
    </group>
  );
}

// ---- Kids inflatable playground (supa_dupa) -----------------------
export function SupaDupa({ w, d, h, baseColor, emissive }) {
  return (
    <group>
      <mesh position={[0, 0.05, 0]} receiveShadow>
        <boxGeometry args={[w, 0.1, d]} />
        <meshStandardMaterial color="#166534" roughness={0.9} />
      </mesh>
      <mesh position={[0, h * 0.35, 0]} castShadow>
        <boxGeometry args={[w * 0.95, h * 0.7, d * 0.95]} />
        <meshStandardMaterial color={baseColor} emissive={baseColor} emissiveIntensity={emissive * 0.3} roughness={0.4} />
      </mesh>
      {[[-1, -1, '#ef4444'], [1, -1, '#3b82f6'], [-1, 1, '#22c55e'], [1, 1, '#a855f7']].map(([x, z, c]) => (
        <mesh key={`t-${x}-${z}`} position={[x * w * 0.35, h + 0.4, z * d * 0.35]} castShadow>
          <cylinderGeometry args={[w * 0.15, w * 0.18, h * 1.2, 8]} />
          <meshStandardMaterial color={c} roughness={0.4} />
        </mesh>
      ))}
      {[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([x, z]) => (
        <mesh key={`tt-${x}-${z}`} position={[x * w * 0.35, h * 1.6 + 0.4, z * d * 0.35]} castShadow>
          <coneGeometry args={[w * 0.2, h * 0.8, 8]} />
          <meshStandardMaterial color="#fbbf24" />
        </mesh>
      ))}
      <mesh position={[0, h * 0.9, d / 2]} rotation={[0, 0, 0]}>
        <torusGeometry args={[w * 0.25, 0.25, 8, 16, Math.PI]} />
        <meshStandardMaterial color="#f59e0b" />
      </mesh>
      {[-1, 0, 1].map((x) => (
        <mesh key={`f-${x}`} position={[x * w * 0.25, h * 2 + 0.5, 0]}>
          <planeGeometry args={[0.6, 0.4]} />
          <meshStandardMaterial color={['#ef4444', '#22c55e', '#3b82f6'][x + 1]} side={THREE.DoubleSide} />
        </mesh>
      ))}
    </group>
  );
}

// ---- Local Authority garden plot (plot_pbt) -----------------------
export function PlotPBT({ w, d, h, baseColor, emissive }) {
  return (
    <group>
      <mesh position={[0, 0.1, 0]} receiveShadow>
        <boxGeometry args={[w, 0.2, d]} />
        <meshStandardMaterial color="#84cc16" roughness={0.9} />
      </mesh>
      {[[-1,-1],[1,-1],[-1,1],[1,1]].map(([x,z]) => (
        <mesh key={`f-${x}-${z}`} position={[x * w/2, 1, z * d/2]} castShadow>
          <cylinderGeometry args={[0.1, 0.1, 2, 6]} />
          <meshStandardMaterial color="#7c5e3c" />
        </mesh>
      ))}
      {[[-0.3, 0.3],[0.3, 0.3],[-0.3, -0.3],[0.3, -0.3]].map(([px, pz], i) => (
        <group key={`plant-${px}-${pz}`} position={[px * w, 0.2, pz * d]}>
          <mesh position={[0, 0.7, 0]} castShadow>
            <cylinderGeometry args={[0.15, 0.2, 1.4, 6]} />
            <meshStandardMaterial color="#5c4033" />
          </mesh>
          <mesh position={[0, 1.7, 0]} castShadow>
            <coneGeometry args={[0.8, 1.5, 8]} />
            <meshStandardMaterial color={['#16a34a', '#22c55e', '#65a30d', '#4ade80'][i]} emissive={baseColor} emissiveIntensity={emissive * 0.2} />
          </mesh>
        </group>
      ))}
      <mesh position={[0, 1.5, d/2]} castShadow>
        <boxGeometry args={[2, 1, 0.05]} />
        <meshStandardMaterial color="#f97316" emissive={baseColor} emissiveIntensity={emissive * 0.4} />
      </mesh>
    </group>
  );
}

// ---- MyKiosk small colorful stall ---------------------------------
export function MyKiosk({ w, d, h, baseColor, emissive }) {
  return (
    <group>
      <mesh position={[0, 0.1, 0]} receiveShadow>
        <boxGeometry args={[w, 0.2, d]} />
        <meshStandardMaterial color="#4a4a4a" />
      </mesh>
      <mesh position={[0, h/2 - 0.3, 0]} castShadow>
        <boxGeometry args={[w, h - 0.6, d]} />
        <meshStandardMaterial color={baseColor} emissive={baseColor} emissiveIntensity={emissive * 0.3} />
      </mesh>
      <mesh position={[0, h - 0.2, d/2 + 0.3]} rotation={[Math.PI / 6, 0, 0]} castShadow>
        <boxGeometry args={[w + 0.4, 0.1, 1.5]} />
        <meshStandardMaterial color="#dc2626" />
      </mesh>
      <mesh position={[0, h - 0.3, d/2 + 0.05]} castShadow>
        <boxGeometry args={[w * 0.8, 0.4, 0.05]} />
        <meshBasicMaterial color="#fbbf24" />
      </mesh>
    </group>
  );
}
